import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';

const DEFAULT_NAV_ITEMS = [
  { label: 'Stålkonstruktioner', url: '/staalkonstruktioner' },
  { label: 'Rækværk', url: '/raekvaerk' },
  { label: 'Trapper', url: '/trapper' },
  { label: 'Porte', url: '/porte' },
  { label: 'Hegn', url: '/hegn' },
  { label: 'Altan', url: '/altan' },
  { label: 'Galleri', url: '/galleri' },
  { label: 'Referencer', url: '/referencer' },
  { label: 'Kontakt os', url: '/kontakt', is_cta: true }
];

export default function Navbar() {
  const [navItems, setNavItems] = useState(DEFAULT_NAV_ITEMS);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/navigation?sort=order&order=asc')
      .then((res) => {
        if (!res.ok) throw new Error('CMS request failed');
        return res.json();
      })
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          const sorted = data.items.sort((a, b) => (a.order || 0) - (b.order || 0));
          setNavItems(sorted);
        }
      })
      .catch(() => {
        // Fallback is maintained
      });
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200 shadow-xs">
      {/* Top info bar */}
      <div className="bg-stone-900 text-stone-300 text-xs py-2 px-4 border-b border-stone-800">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-6">
            <a
              href="tel:+4548796816"
              className="flex items-center gap-2 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-amber-500" aria-hidden="true"></i>
              <span className="font-semibold tracking-wide">+45 48 79 68 16</span>
            </a>
            <a
              href="tel:61604241"
              className="hidden sm:flex items-center gap-2 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-mobile-screen text-amber-500" aria-hidden="true"></i>
              <span className="font-semibold tracking-wide">(+45) 61 60 42 41</span>
            </a>
            <a
              href="mailto:lars@gribskovsmeden.dk"
              className="flex items-center gap-2 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-amber-500" aria-hidden="true"></i>
              <span>lars@gribskovsmeden.dk</span>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-2 text-stone-400">
            <i className="fa-solid fa-location-dot text-amber-500" aria-hidden="true"></i>
            <a
              href="https://www.google.com/maps/place/Rundinsvej%2045,%203200%20Helsinge"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors whitespace-nowrap"
            >
              Rundinsvej 45, 3200 Helsinge
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group py-2 max-w-[200px] sm:max-w-[300px]"
            aria-label="Gribskovsmeden ApS Forside"
          >
            <img
              src="https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/logo-10.png"
              alt="Gribskovsmeden ApS Logo"
              className="max-w-[200px] sm:max-w-[300px] w-auto max-h-12 sm:max-h-14 h-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation: Visible on screens >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-1.5 lg:gap-2">
            {navItems.map((item) => {
              if (item.is_cta) {
                return (
                  <NavLink
                    key={item.url || item.label}
                    to={item.url}
                    className={({ isActive }) =>
                      `ml-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${
                        isActive
                          ? 'bg-amber-600 text-white shadow-amber-600/20'
                          : 'bg-stone-900 hover:bg-stone-800 text-white'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              }

              return (
                <NavLink
                  key={item.url || item.label}
                  to={item.url}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors relative whitespace-nowrap ${
                      isActive
                        ? 'text-stone-950 font-bold bg-stone-100/90 shadow-xs'
                        : 'text-stone-700 hover:text-stone-950 hover:bg-stone-50'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span>{item.label}</span>
                      {isActive && (
                        <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-amber-600 rounded-full"></span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Burger Menu Button: Visible on screens < 1200px */}
          <div className="min-[1200px]:hidden flex items-center gap-3">
            <a
              href="tel:+4548796816"
              className="hidden sm:flex px-3 py-1.5 bg-stone-100 text-stone-900 rounded-lg text-xs font-semibold hover:bg-stone-200 transition-colors items-center gap-1.5 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-amber-600" aria-hidden="true"></i>
              <span>Ring op</span>
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-lg text-stone-800 hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors cursor-pointer"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-stone-200 bg-white shadow-xl animate-in slide-in-from-top duration-200">
          <div className="px-4 pt-3 pb-6 space-y-1 max-h-[80vh] overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.url || item.label}
                to={item.url}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-amber-50 text-amber-900 font-bold border-l-4 border-amber-600'
                      : 'text-stone-700 hover:bg-stone-50 hover:text-stone-950'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <div className="pt-4 mt-4 border-t border-stone-200 space-y-3">
              <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider px-4">
                Direkte kontakt
              </div>
              <a
                href="tel:+4548796816"
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-stone-900 bg-stone-50 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-600 w-5" aria-hidden="true"></i>
                <span>+45 48 79 68 16</span>
              </a>
              <a
                href="tel:61604241"
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-stone-900 bg-stone-50 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-mobile-screen text-amber-600 w-5" aria-hidden="true"></i>
                <span>(+45) 61 60 42 41 (Mobil)</span>
              </a>
              <a
                href="mailto:lars@gribskovsmeden.dk"
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-stone-800 bg-stone-50 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-amber-600 w-5" aria-hidden="true"></i>
                <span>lars@gribskovsmeden.dk</span>
              </a>
              <div className="px-4 py-2 text-xs text-stone-500">
                <i className="fa-solid fa-location-dot text-amber-600 mr-1.5" aria-hidden="true"></i>
                Rundinsvej 45, 3200 Helsinge
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
